/**
 * Replacing Google Meet with a Bundle meeting (docs/features/calendar-meet-swap.md): what the event page, Settings →
 * Calendar and Settings → Huddles show, as pure rules every platform shares (MeetSwapViewModel, MeetPolicyViewModel).
 */
import type { CalendarEvent, MeetPolicyRun, MeetSwapCandidatesResponse, MeetSwapRun, MeetSwapRunStatus, MeetSwapSkipReason } from "@bundle/contracts";
import type { MessageKey } from "../i18n.ts";
import { meetSwapCandidate } from "./calendarInvite.ts";

/** How often a running run is asked for its progress (the API sends no realtime updates for runs). */
export const MEET_SWAP_POLL_MS = 2000;

type SwapEvent = Parameters<typeof meetSwapCandidate>[0];

/** The one-time prompt shows on this event's page: I organise it, it has Meet, nobody answered yet, no policy, no run. */
export function showMeetPrompt(e: SwapEvent, me: string, now: number, c: MeetSwapCandidatesResponse | undefined): boolean {
  return c !== undefined && c.prompt === "ask" && c.policy === "member" && c.activeRunId === undefined && meetSwapCandidate(e, me, now);
}

/** The ⋯ menu's "Use a Bundle meeting": after the prompt was answered, while each person chooses. */
export function canUseBundleMeeting(e: SwapEvent, me: string, now: number, c: MeetSwapCandidatesResponse | undefined): boolean {
  return c !== undefined && c.prompt !== "ask" && c.policy === "member" && meetSwapCandidate(e, me, now);
}

/** Guests outside Bundle who get an updated invite (people with only an email address). */
export function outsideGuests(e: Pick<CalendarEvent, "attendees">): number {
  return e.attendees.filter((a) => a.userId === undefined && a.email !== undefined).length;
}

/** Still going: held (Undo window), running or paused. */
export function runActive(status: MeetSwapRunStatus): boolean {
  return status === "held" || status === "running" || status === "paused";
}

/** Events handled so far (moved, skipped or failed). */
export function runHandled(r: Pick<MeetSwapRun, "done" | "skipped" | "failed">): number {
  return r.done + r.skipped + r.failed;
}

/** 0…1 for the Progress bar. */
export function runFraction(r: Pick<MeetSwapRun, "total" | "done" | "skipped" | "failed">): number {
  return r.total <= 0 ? 0 : Math.min(1, runHandled(r) / r.total);
}

/** The toast's Undo still cancels everything (nothing sent yet). */
export function undoOpen(r: Pick<MeetSwapRun, "status" | "startsAt">, now: number): boolean {
  return r.status === "held" && r.startsAt > now;
}

/** "Put the Google Meet links back" applies: a finished swap that moved something, within its 30 days. */
export function canRestore(r: Pick<MeetSwapRun, "kind" | "status" | "done" | "restoreUntil">, now: number): boolean {
  return r.kind === "swap" && r.status === "done" && r.done > 0 && (r.restoreUntil === undefined || r.restoreUntil > now);
}

/** Events left out because Google's write scope is missing ("An admin needs to let Bundle edit calendars"). */
export function scopeSkipped(r: Pick<MeetSwapRun, "skippedEvents">): number {
  return r.skippedEvents.filter((s) => s.reason === "google_scope").length;
}

const SKIP_KEYS: Record<MeetSwapSkipReason, MessageKey> = {
  google_scope: "calendar.meetSwap.skip.googleScope",
  google_error: "calendar.meetSwap.skip.googleError",
  not_organizer: "calendar.meetSwap.skip.notOrganizer",
  no_meet: "calendar.meetSwap.skip.noMeet",
  ended: "calendar.meetSwap.skip.ended",
  gone: "calendar.meetSwap.skip.gone",
  no_mailbox: "calendar.meetSwap.skip.noMailbox",
  mail_error: "calendar.meetSwap.skip.mailError",
};

/** Why an event was left out, as a message key. */
export function skipReasonKey(reason: MeetSwapSkipReason): MessageKey {
  return SKIP_KEYS[reason];
}

/** A policy run's totals: people whose run finished, events moved / to move, and the bar's fraction. */
export type PolicyProgress = { people: number; peopleTotal: number; done: number; total: number; skipped: number; fraction: number };

export function policyProgress(r: Pick<MeetPolicyRun, "members">): PolicyProgress {
  let done = 0;
  let total = 0;
  let skipped = 0;
  let handled = 0;
  let people = 0;
  for (const m of r.members) {
    done += m.done;
    total += m.total;
    skipped += m.skipped;
    handled += runHandled(m);
    if (!runActive(m.status)) people++;
  }
  const allDone = r.members.length > 0 && people === r.members.length;
  return { people, peopleTotal: r.members.length, done, total, skipped, fraction: total <= 0 ? (allDone ? 1 : 0) : Math.min(1, handled / total) };
}
